import { ElementRef, Injectable, Injector, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom, from, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import * as _ from 'lodash';
import {
  TrackChangesEditing,
  DocumentFragment,
} from '@ckeditor-build/ckeditor.js';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommonConstants, HtmlAttribute, LocalStorage } from '../shared';
import { DiacriticResolver } from '../shared/diacritic.resolver';
import { WKMessageDialogComponent } from '../components/shared/dialogs/message-dialog/message.dialog';
import { WKMessageDialogService } from '../components/shared/dialogs/message-dialog/message.dialog.service';
import { CKEditorComponent } from '../components/ckeditor';
import { CitationUnnumberedRefModel } from '../model/citation.model';
import { AppStore, ICopyKeyword } from '../store/states';
import {
  getFeatureFlags,
  getKeywordById,
} from '../store/selectors/app.custom-selectors';
import { EditCopiedKeyword } from '../store/actions/app.custom-actions';
import { WkKeywordService } from './wk-keyword.service';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import {
  CommentCategoryEnum,
  CustomComment,
  CustomCommentThread,
  ISuggestion,
  WKUserGroupAPI,
} from '../model/custom-models';
import { SiteConfig, ToolbarItems } from '../types';
import { WkReleaseNoteService } from './wk-release-note.service';
import { WkTranslationService } from './wk-translation.service';
import { SiteConfigService } from './site-config.service';
import { FEATURE_FLAGS } from '../constants';
import { CitationService, ReferenceTypes } from './citations.service';
import { WKDeleteDecoratorsDialogComponent } from '../components/shared/dialogs/message-dialog';
import {
  setCMTitleValue,
  getBaseHrefForLinkPlugin,
} from '../modules/wk-document-decorator';
import { CkeditorConstants } from '../components/ckeditor/constants/ckeditor.constants';
const { ID } = HtmlAttribute;

const { CURRENT_SITE, CURRENT_SITE_DATA } = LocalStorage;

const {
  CK_PLUGINS: { INSERT_LINK, TRACK_CHANGES, TRACK_CHANGES_EDITING },
  ID_ATTR,
  linkIconClassInMainEditor,
  SKOS_EDITORIAL_NOTE,
  REL,
  parentCitationPlaceholder,
  NUMBERED_CITATION,
  NUMBERED_CITATION_QUERYSELECTOR,
  TEXT_FORMATTING_PLUGINS,
  inlineMediaClass,
} = CommonConstants;

const {
  MEDIA_AND_INLINE_TAGS: {
    INLINE_IMAGE,
    INLINE_AUDIO,
    FIGURE_TAG,
    MEDIA_CLASS,
  },
  class: { CK_MEDIA_IMAGE, CK_MEDIA_VIDEO },
} = CkeditorConstants;
const MEDIA_SELECTION_DISABLED = 'media-selection';
export interface RemoveCommentEventData {
  threadId: string;
}

@Injectable({
  providedIn: 'root',
})
export class WkEditorService implements OnDestroy {
  private readonly specialCharCheck = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
  unNumberedRefsToSort: CitationUnnumberedRefModel[];
  filteredObjects: CitationUnnumberedRefModel[];
  diacriticResolver = DiacriticResolver;
  suggestionsToDiscard = [];
  resolvedCommentThreads = [];
  contentType = ['application/json'];
  toolbarItems = CommonConstants.toolbarItems;
  private readonly parser = new DOMParser();
  private readonly messageService: WKMessageDialogService;
  private readonly apiService: WkAlfrescoApiService;
  private readonly store: Store<AppStore>;
  private readonly releaseNoteService: WkReleaseNoteService;
  private readonly translationService: WkTranslationService;
  private readonly siteConfig: SiteConfigService;
  private readonly citationService: CitationService;
  private docBaseHref: string;
  private readonly onDestroy$ = new Subject<void>();

  constructor(private readonly injector: Injector) {
    this.apiService =
      this.injector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
    this.messageService = this.injector.get<WKMessageDialogService>(
      WKMessageDialogService
    );
    this.store = this.injector.get<Store<AppStore>>(Store);
    this.releaseNoteService =
      this.injector.get<WkReleaseNoteService>(WkReleaseNoteService);
    this.translationService =
      this.injector.get<WkTranslationService>(WkTranslationService);
    this.siteConfig = this.injector.get<SiteConfigService>(SiteConfigService);
    this.citationService = this.injector.get<CitationService>(CitationService);
    this.store
      .select(getBaseHrefForLinkPlugin)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((baseHrefForLinkPlugin: string) => {
        this.docBaseHref = baseHrefForLinkPlugin;
      });
  }

  /**
   * @param ckeditor This function is for pasting link when there is a bulleted list text and shouldn't treat as new bullet / pasting any data with releasenotes and should not consider the releasenotes
   * @param content
   */

  private onPasteContent(
    ckeditor: CKEditorComponent,
    content: DocumentFragment
  ): void {
    const selection = ckeditor.editorInstance.model.document.selection;
    const isSelectionParentHasListStyle = selection
      ?.getFirstPosition()
      ?.parent?.hasAttribute(CommonConstants.listStyle);
    const pastedContent =
      (content._children &&
        _.filter(
          [...content.getChildren()],
          (item) =>
            item.name === CommonConstants.spanTagName ||
            item.name === CommonConstants.RELEASE_NOTES_TAG_NAME
        )) ||
      [];

    //checking whether pasted content is a webresource or media resource or link to other document
    for (const pastedItem of pastedContent) {
      const typeOfAttribute = pastedItem.getAttribute(
        CommonConstants.typeOfAttribute
      );
      const isItemOfTypeLink =
        typeOfAttribute === CommonConstants.pciCoreWebResource ||
        typeOfAttribute === CommonConstants.pciCoreBibliographicResource ||
        typeOfAttribute === CommonConstants.pciCoreMediaResource;

      //removing the additional attributes which creates the bullet list
      if (
        isSelectionParentHasListStyle &&
        pastedItem.getAttribute(CommonConstants.REL) ===
          CommonConstants.pciCoreLinks &&
        isItemOfTypeLink
      ) {
        ckeditor.editorInstance.model.change((writer) => {
          _.forEach(CommonConstants.attributeToRemove, (attr) => {
            writer.removeAttribute(attr, pastedItem);
          });
        });
      }
      //not allowing release note icon to be pasted when copied with content
      else if (
        pastedItem.getAttribute(CommonConstants.REL) ===
        CommonConstants.SKOS_EDITORIAL_NOTE
      ) {
        ckeditor.editorInstance.model.enqueueChange(
          { isUndoable: false },
          (writer) => {
            writer.remove(pastedItem);
          }
        );
      }
    }
  }

  public sanitizeCitationString(citationString): string {
    let firstChar = null;
    citationString = citationString
      .replace(/ /g, '')
      .replace(/<(.|\n)*?>/g, '');
    if (
      Number.isInteger(+citationString[0]) ||
      this.specialCharCheck.test(citationString[0])
    ) {
      firstChar = citationString[0];
    }
    citationString = citationString.replace(/[^a-zA-Z]/g, '');
    if (firstChar) {
      citationString = `${firstChar}${citationString}`;
    }
    return citationString;
  }

  /**
   * This function is for listening the ckeditor events from ckeditor plugins
   */
  public accessCkeditorEventsFromPlugin(ckeditor: CKEditorComponent): void {
    try {
      ckeditor?.editorInstance?.listenTo(
        ckeditor.editorInstance.model,
        'insertContent',
        (_evt, [content, _selectable]) => {
          this.onPasteContent(ckeditor, content);
        },
        { priority: 3 }
      );
    } catch (err) {
      console.log(err);
    }
  }

  public createAnnotations(annotation): boolean {
    /**
     * Returns true for editorial comments
     */
    if (annotation.type === CommonConstants.comment) {
      return !this.resolvedCommentThreads.includes(
        annotation.view.mainView.element.getAttribute('data-thread-id')
      );
    }

    /**
     * Checks if annotation has Split: span and Split: list description and hides in right hand panel view
     */
    if (annotation.innerView?.descriptionParts?.length === 1) {
      const annotationContent =
        annotation.innerView.descriptionParts[0].content;
      const hideAnnotationArray = [
        CommonConstants.splitSpan,
        CommonConstants.splitPara,
      ];
      const isAnnotationSplitSpanOrList =
        hideAnnotationArray.includes(annotationContent);
      if (isAnnotationSplitSpanOrList) {
        this.suggestionsToDiscard.push(
          annotation.view.mainView.template.attributes[
            CommonConstants.dataSuggestionIdAttribute
          ][0]
        );
      }
      return !isAnnotationSplitSpanOrList;
    } else {
      return true;
    }
  }

  public generateCustomGuid(editor: CKEditorComponent): string {
    editor.editorInstance.execute('generatecustomguid');
    return localStorage.getItem('guid');
  }

  /**
   * Get selected content as string from ckeditor
   *
   * @public
   * @param {CKEditorComponent} ckeditor
   * @returns {string}
   */
  public getSelectedHTMLString(ckeditor: CKEditorComponent): string {
    return ckeditor.editorInstance.data.stringify(
      ckeditor.editorInstance.model.getSelectedContent(
        ckeditor.editorInstance.model.document.selection
      )
    );
  }

  /**
   * Checking if selection is contains links, citations or keywords
   *
   * @private
   * @param {CKEditorComponent} ckeditor
   * @returns {(boolean | string)}
   */
  private validateSelectedContent(ckeditor: CKEditorComponent): boolean {
    let result = true;
    const selectedHtml = this.getSelectedHTMLString(ckeditor);
    for (const attribute of CommonConstants.GET_ATTRIBUTE_NOT_ALLOW_FOR_KEYWORD()) {
      if (selectedHtml.includes(attribute)) {
        result = false;
        break;
      }
    }
    if (result) {
      const ancestors =
        ckeditor.editorInstance.editing.view.document.selection.anchor
          .getAncestors()
          .reverse();
      for (const node of ancestors) {
        if (
          node.name &&
          (node.getAttribute(HtmlAttribute.REL) ===
            CommonConstants.pciCoreLinks ||
            node.getAttribute(HtmlAttribute.TYPE_OF) ===
              CommonConstants.pciCoreWebResource ||
            node.getAttribute(HtmlAttribute.TYPE_OF) ===
              CommonConstants.pciCoreBibliographicResource ||
            node.getAttribute(HtmlAttribute.TYPE_OF) ===
              CommonConstants.pciCoreMediaResource ||
            node.getAttribute(HtmlAttribute.CLASS) ===
              CommonConstants.wkKeyword)
        ) {
          result = false;
          break;
        }
      }
    }
    return result;
  }

  /**
   * Description placeholder
   *
   * @date 9/11/2023 - 5:10:38 PM
   *
   * @async
   * @param {{ copiedKeyword: IKeyWord; ckeditor: CKEditorComponent; keywordService: WkKeywordService; }} { copiedKeyword, ckeditor, keywordService }
   * @returns {Promise<void>}
   */
  public async onCreateCopy({
    copiedKeyword,
    ckeditor,
    keywordService,
    callback,
  }: {
    copiedKeyword: ICopyKeyword;
    ckeditor: CKEditorComponent;
    keywordService: WkKeywordService;
    callback: () => void;
  }): Promise<void> {
    const isValid = this.validateSelectedContent(ckeditor);
    const selectedHtml = this.getSelectedHTMLString(ckeditor);
    const { event, keyword } = copiedKeyword;
    const keywordTextToAdd =
      selectedHtml.trim() !== '' ? selectedHtml : keyword.keyTerm;
    if (isValid && keywordTextToAdd.trim() !== '') {
      keywordService.setDefId = {
        defId: event
          ? event.arguments
              .getAttribute(HtmlAttribute.RESOURCE)
              .split('/')
              .pop()
          : keyword.id,
        labelId: event
          ? event.arguments.getAttribute(HtmlAttribute.ID)
          : this.generateCustomGuid(ckeditor),
      };
      const defId = event ? keyword.id : keywordService.getDefId.defId;
      let keywordToCopy = await firstValueFrom(
        this.store.select(getKeywordById(defId)).pipe(take(1))
      );
      keywordToCopy = {
        ...keywordToCopy,
        keyWordString:
          keywordToCopy.keyWordString +
          keywordService.getAlternateLabelTemplate(keywordTextToAdd),
      };
      !event && callback();
      this.store.dispatch(new EditCopiedKeyword(keywordToCopy));
    } else {
      keywordTextToAdd.trim() !== '' &&
        this.messageService.openMessage(WKMessageDialogComponent, {
          message: 'APP.KEYWORDS_DEFINITION.COPY_MESSAGE_DIALOG.MESSAGE',
          headerLabel: 'APP.KEYWORDS_DEFINITION.COPY_MESSAGE_DIALOG.HEADER',
          okLabel: 'APP.COMMON.OK',
        });
    }
  }

  /**
   * Fetches the array of ids of the links and removes the track changes marker on the respective links
   *
   * @public
   * @param {CKEditorComponent} ckeditor
   * @param {TrackChangesEditing} trackChangesEditingPlugin
   * @param {ElementRef} elementRef
   * @returns {void}
   */
  public removeMarkerOnLink(
    ckeditor: CKEditorComponent,
    trackChangesEditingPlugin: TrackChangesEditing,
    elementRef: ElementRef
  ): void {
    const removeMarkerLinkIds = localStorage.getItem(
      LocalStorage.REMOVE_MARKER_FROM_LINK
    );
    if (removeMarkerLinkIds) {
      ckeditor?.editorInstance.model.change((writer) => {
        _.forEach(
          Array.from(JSON.parse(removeMarkerLinkIds)),
          (linkId: string) => {
            const linkElement = elementRef.nativeElement.querySelector(
              `[id='${linkId}']`
            );
            const suggestion =
              linkElement &&
              trackChangesEditingPlugin.getSuggestion(
                linkElement.children[0].getAttribute(
                  CommonConstants.trackchangesMarkup.SUGGESTION
                )
              );
            const [markerName] = suggestion && suggestion.getMarkerNames();
            if (writer.model.markers.has(markerName)) {
              writer.removeMarker(markerName);
            }
          }
        );
      });
      localStorage.removeItem(LocalStorage.REMOVE_MARKER_FROM_LINK);
    }
  }

  private getDisplayNames(data: WKUserGroupAPI): string[] {
    if (data) {
      return _.map(data.list.entries, (item: any) => item.entry.id);
    } else {
      return [];
    }
  }

  public callPeopleApi(): Observable<any> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: 'alfresco/api/-default-/public/alfresco/versions/1/people/-me-/groups?skipCount=0&maxItems=100',
        httpMethod: 'GET',
        contentTypes: [''],
        accepts: [''],
      })
    ).pipe(map((data: WKUserGroupAPI) => this.getDisplayNames(data)));
  }

  public async getToolBarItems(
    isTemplateDoc: boolean,
    toolBarConfig: Map<string, boolean>
  ): Promise<ToolbarItems> {
    const isAiEnabled = await firstValueFrom(
      this.store.select(getFeatureFlags).pipe(take(1))
    );
    const toolbarItemsUpdated = [...this.toolbarItems];
    if (isTemplateDoc) {
      toolbarItemsUpdated.splice(this.toolbarItems.indexOf('heading'), 1);
    } else {
      toolbarItemsUpdated.splice(
        this.toolbarItems.indexOf('hidedataignoreattribute'),
        1
      );
    }

    toolBarConfig?.forEach((value: boolean, key: string) => {
      !value && toolbarItemsUpdated.splice(toolbarItemsUpdated.indexOf(key), 1);
    });

    if (isAiEnabled[FEATURE_FLAGS.ENABLE_APOLLO_AI_FEATURE]) {
      return {
        toolbar: {
          items: toolbarItemsUpdated,
        },
      };
    }
    const assistantIdx = toolbarItemsUpdated.indexOf('aiAssistant');
    if (assistantIdx !== -1) {
      toolbarItemsUpdated.splice(assistantIdx, 1);
    }
    const aiCommandsIdx = toolbarItemsUpdated.indexOf('showAICommands');
    if (aiCommandsIdx !== -1) {
      toolbarItemsUpdated.splice(aiCommandsIdx, 1);
    }
    return {
      toolbar: {
        items: toolbarItemsUpdated,
      },
    };
  }

  public updateCitationFlag(nodeId: string, content: boolean): Observable<any> {
    const data = {
      properties: {
        'apollo:isCitationValid': content,
      },
    };
    return from(
      this.apiService.callContentClientCustomApi({
        path: `alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}`,
        httpMethod: 'PUT',
        bodyParam: data,
        contentTypes: this.contentType,
        accepts: this.contentType,
      })
    );
  }

  /**
   * Removes attributes that starts with 'html' created by ckeditor before save
   * For instance htmlpattribute, htmldivattribute
   * @param editorDataBody Document
   */
  public removeHtmlAttr(editorDataBody: Document): void {
    _.forEach(
      Array.from(editorDataBody.body.querySelectorAll('*')),
      (ele: HTMLElement) =>
        _.forEach(Array.from(ele.attributes), ({ name }) => {
          if (
            name.startsWith(CommonConstants.html) &&
            name.includes(CommonConstants.attributes)
          ) {
            ele.removeAttribute(name);
          }
        })
    );
  }

  /**
   * Removes the release note form the document
   * @private
   * @param {CKEditorComponent} ckeditor
   * @param {string} threadId
   * @returns {void}
   */
  private removeReleaseNoteFromDocument(
    ckeditor: CKEditorComponent,
    threadId: string
  ): void {
    ckeditor?.editorInstance.model.enqueueChange(
      { isUndoable: false },
      (writer) => {
        const range = ckeditor.editorInstance.model.createRangeIn(
          ckeditor.editorInstance.model.document.getRoot()
        );
        const releaseNoteToRemove = [
          ...range.getItems({ ignoreElementEnd: true }),
        ].find(
          (item) => item.hasAttribute(ID) && item.getAttribute(ID) === threadId
        );
        releaseNoteToRemove && writer.remove(releaseNoteToRemove);
      }
    );
  }

  /**
   * Removes a Comment Attributes/Id's in editor document/template,
   * which is not removed by any action of remove/resolve in comment threads(Sidebar).
   *
   * @param { RemoveCommentEventData } data - contains thread id.
   * @param { CKEditorComponent } ckeditor - editor instance.
   */
  public removeFaultyCommentAttr(data: any, ckeditor: CKEditorComponent): void {
    const { model } = ckeditor.editorInstance;
    const range = model.createRangeIn(model.document.getRoot());
    const itemsInRange = range.getItems({ ignoreElementEnd: true });

    for (const item of itemsInRange) {
      if (
        item.name === NUMBERED_CITATION &&
        item.hasAttribute('class') &&
        item.getAttribute('class').includes('numbered-citation')
      ) {
        this.removeThreadIdInAttr(item, data.threadId, ckeditor);
      }
    }
    this.removeReleaseNoteFromDocument(ckeditor, data.threadId);
  }

  private removeThreadIdInAttr(
    item,
    threadId: string,
    ckeditor: CKEditorComponent
  ): void {
    const { model } = ckeditor.editorInstance;
    model.change((writer) => {
      for (const commentAttr of CommonConstants.commentsMarkup) {
        if (item?.getAttribute(commentAttr)?.includes(threadId)) {
          const commentAttrInItem = item.getAttribute(commentAttr);
          if (commentAttrInItem.includes(',')) {
            // If comments attribute contains " , " then multiple thread id's attached to certain element.
            _.forEach(commentAttrInItem.split(','), (thread) => {
              if (thread.includes(threadId)) {
                writer.setAttribute(
                  commentAttr,
                  commentAttrInItem
                    .replace(thread, '')
                    .replace(/\,+/g, ',')
                    .replace(/^\s*,+\s*|\s*,+\s*$/g, ''), //removing trailing commas and possible spaces at the start and end
                  item
                );
              }
            });
          } else if (commentAttrInItem.includes(threadId)) {
            writer.removeAttribute(commentAttr, item);
            return;
          }
        }
      }
    });
  }

  /**
   * Updates Comment thread payload request as per rdfa.
   * @public
   * @param {CustomCommentThread}
   * @param {ISuggestion[]}
   * @param {string[]}
   * @returns {CustomComment[]}
   */
  public getCommentsPayload(
    commentThread: CustomCommentThread,
    suggestionsData: ISuggestion[],
    data: { addedReleaseNoteIds: string[]; addedUMCReleaseNoteIds: string[] }
  ): CustomComment[] {
    const suggestionIds: string[] = _.map(
      _.filter(suggestionsData, (suggestion) => suggestion.hasComments),
      (suggestion) => suggestion.id
    );
    const isReleaseNote = data.addedReleaseNoteIds.includes(
      commentThread.threadId
    );
    const isUMCReleaseNote = data.addedUMCReleaseNoteIds.includes(
      commentThread.threadId
    );
    return _.map(commentThread.comments, (comment: CustomComment) => {
      let modifiedContent = comment.content;

      switch (true) {
        case !isReleaseNote && !isUMCReleaseNote: {
          // Parse the comment content as an HTML document
          const doc = this.getParsedHTMLData(modifiedContent);
          // Select all <p> tags and set the lang="en" attribute
          _.forEach(
            this.getParsedHTMLData(modifiedContent).querySelectorAll('p'),
            (pElement) => {
              pElement.setAttribute('lang', 'en'); // Add or modify the lang attribute
            }
          );
          // Convert the modified document back to a string
          modifiedContent = doc.body.innerHTML;
          break;
        }

        case isReleaseNote && !isUMCReleaseNote: {
          modifiedContent = this.releaseNoteService.getReleaseNoteTemplate(
            commentThread.threadId,
            this.getParsedHTMLData(commentThread.comments[0].content).body
              .innerText
          );
          break;
        }

        case isUMCReleaseNote && !isReleaseNote: {
          console.log(
            'Generating UMC Release Note Template',
            comment.attributes
          );
          comment.attributes['isAIGeneratedUMC'] = true;
          comment.attributes['isUserReviewed'] = true;
          break;
        }
      }
      return {
        ...comment,
        content: modifiedContent, // Use the modified content
        attributes: {
          ...(!(
            isReleaseNote ||
            isUMCReleaseNote ||
            commentThread.comments[0].attributes.commentType ===
              CommentCategoryEnum.RELEASE_NOTES
          ) && { status: 'draft' }),
          ...comment.attributes,
          commentType: suggestionIds.includes(commentThread.threadId)
            ? CommentCategoryEnum.TRACK_CHANGE
            : isReleaseNote
            ? this.translationService.instant(
                this.getCurrentSiteDetails().commentTab.releasenote.commentType
              )
            : commentThread.comments[0].attributes.commentType ||
              CommentCategoryEnum.EDITORIAL,
        },
      };
    });
  }

  public getParsedHTMLData(data: string): Document {
    return this.parser.parseFromString(data, 'text/html');
  }

  /**
   * Fetches current site details and stores in localStorage
   * @public
   * @returns {SiteConfig}
   */
  public getCurrentSiteDetails(): SiteConfig {
    const currentSite = localStorage.getItem(CURRENT_SITE);
    const currSiteObj: SiteConfig =
      this.siteConfig.getCurrentSiteObject(currentSite);
    localStorage.setItem(CURRENT_SITE_DATA, JSON.stringify(currSiteObj));
    return currSiteObj;
  }

  /**
   * Removing the un posted releasenotes pin and threads.
   * @param { CKEditorComponent } editor.
   * @param { string } editorData.
   */
  public sanitizeReleaseNotes(
    editor: CKEditorComponent,
    editorData: string
  ): string {
    const nonSubmittedNotes = [];
    const commentsRepository =
      editor.editorInstance?.plugins?.get('CommentsRepository');
    const formattedEditorData = this.getParsedHTMLData(editorData);
    const releaseNotesPins = formattedEditorData?.body?.querySelectorAll(
      `[${REL}='${SKOS_EDITORIAL_NOTE}']`
    );
    _.forEach(releaseNotesPins, (note: HTMLElement) => {
      const noteId = note?.getAttribute?.(ID_ATTR)?.toString();
      !commentsRepository?.getCommentThread?.(noteId)?.isSubmitted &&
        nonSubmittedNotes.push(noteId);
    });

    return this.removeUnPostedNotes(nonSubmittedNotes, formattedEditorData);
  }
  /**
   * Defining a remove release notes method by passing threadId of notes and editor Instance.
   * @param {string[]} removableNoteIds.
   * @param { Document } formattedEditorData.
   */
  private removeUnPostedNotes(
    removableNoteIds: string[],
    formattedEditorData: Document
  ): string {
    _.forEach(removableNoteIds, (id) => {
      const element = formattedEditorData?.body?.querySelector(
        `[${ID_ATTR}='${id}']`
      );
      element?.remove?.();
    });
    return formattedEditorData.body.innerHTML.toString();
  }

  /**
   * Enables / Disables plugins based on selection focus in document.
   * @param {string} command.
   * @param { ElementRef } elementRef.
   * @param { MouseEvent } event.
   * @returns { void }
   */
  public enableDisablePlugin(
    command: string,
    elementRef: ElementRef,
    event: MouseEvent
  ): void {
    if (this.citationService.ckeditorInstanceFromEditorComp) {
      const {
        commands,
        editing: {
          view: {
            document: { isFocused },
          },
        },
        model: {
          document: { selection },
        },
      } = this.citationService.ckeditorInstanceFromEditorComp;
      if (command === INSERT_LINK) {
        if (
          (isFocused && selection.getFirstPosition().parent?.name) ||
          elementRef.nativeElement
            ?.querySelector(linkIconClassInMainEditor)
            ?.contains(event.target)
        ) {
          commands.get(command).isEnabled = true;
        } else {
          this.hideOnBlur(command);
        }
      } else if (!isFocused) {
        this.hideOnBlur(command);
      }
    }
  }

  /**
   * Disables plugin upon blur.
   * @param {string} command.
   * @returns { void }
   */
  public hideOnBlur(command: string): void {
    if (this.citationService.ckeditorInstanceFromEditorComp) {
      const { commands } = this.citationService.ckeditorInstanceFromEditorComp;
      if (commands.get(command)?.isEnabled) {
        commands.get(command).isEnabled = false;
      }
    }
  }

  /**
   * Filters only subsequent refs
   * @param citationItems Citation model element
   * @returns
   */
  public isSubseqCitedRefAvailable(citationItems): Element[] {
    return _.filter(
      citationItems,
      (item) =>
        item.name === NUMBERED_CITATION &&
        item.hasAttribute(parentCitationPlaceholder)
    );
  }

  /**
   * Filters only first cited refs
   * @param citationItems citation model element
   * @returns
   */
  public isSubseqCitedRefNotAvailable(citationItems): Element[] {
    return _.filter(
      citationItems,
      (item) =>
        item.name === NUMBERED_CITATION &&
        !item.hasAttribute(parentCitationPlaceholder)
    );
  }

  /**
   * Finds citations matching with referencedBy array in current selection
   * @param referencedBy string []
   * @returns array of citations
   */
  public checkCitationsInSelection(referencedBy: string[]): Array<Element> {
    const range =
      this.citationService.ckeditorInstanceFromEditorComp?.model?.createRangeIn(
        this.citationService.ckeditorInstanceFromEditorComp?.model?.document?.getRoot()
      );

    // Finds all references of citation deleted from RHP

    return _.filter([...range?.getItems({ ignoreElementEnd: true })], (item) =>
      referencedBy?.includes(item?.getAttribute?.('id'))
    );
  }

  /**
   * Opens Delete Confirmation dialog for citation
   * @param dialogRef
   * @param multipleRefData
   * @returns
   */
  public getDeleteDialogRefComponent(
    dialogRef: MatDialog,
    multipleRefData: Array<{ citationName: string; citationTotalCount: number }>
  ): MatDialogRef<WKDeleteDecoratorsDialogComponent> {
    return dialogRef.open(WKDeleteDecoratorsDialogComponent, {
      minHeight: 'auto',
      width: '419px',
      panelClass: 'wk-delete-citation-dialog',
      autoFocus: false,
      data: {
        citationEditType: ReferenceTypes.NumberedRef,
        multipleRef: multipleRefData,
      },
    });
  }

  /**
   * Updates the attributes of the found items to reflect a new first cited citation.
   *
   * @param foundItems - An array of items that need to be updated.
   * @param referencedBy - An array of references, where the first element is the new first cited reference.
   *
   * This function updates the `data-parentcitation` and `id` attributes of each item in `foundItems`.
   * The first item in `foundItems` gets its `id` attribute set to the first element of `referencedBy`.
   * Subsequent items get their `data-parentcitation` attribute set to the first element of `referencedBy`,
   * and their `id` attribute set to the corresponding element in the rest of `referencedBy`.
   */
  public updateToANewFirstCitedCitation(
    foundItems: any,
    referencedBy: string[]
  ): void {
    const [firstReferencedBy, ...restReferencedBy] = referencedBy;

    this.citationService.ckeditorInstanceFromEditorComp.model.enqueueChange(
      { isUndoable: false },
      (writer) => {
        _.forEach(foundItems, (item, index) => {
          const isFirstItem = index === 0;
          writer.removeAttribute(parentCitationPlaceholder, item);

          if (isFirstItem) {
            writer.setAttribute('id', firstReferencedBy, item);
          } else {
            writer.setAttribute(
              parentCitationPlaceholder,
              firstReferencedBy,
              item
            );
            writer.setAttribute('id', restReferencedBy[index - 1], item);
          }
        });
      }
    );
  }

  /**
   * Finds citations in the document based on the `resource` attribute value.
   *
   * @param citationString - The string containing the citation information.
   * @param getCitationDataForReOrder - An array containing citation data for reordering.
   *
   * This function searches for citations within the CKEditor document model that match the `resource`
   * attribute value extracted from the `citationString`. It logs the matched value and initializes
   * an array to store the found items.
   */
  public findCitationsInDocViaResourceAttrVal(
    citationString,
    getCitationDataForReOrder
  ): void {
    const range =
      this.citationService.ckeditorInstanceFromEditorComp.model.createRangeIn(
        this.citationService.ckeditorInstanceFromEditorComp.model.document.getRoot()
      );
    const str = citationString;
    const regex = /about="([^"]+)"/;
    const match = str.match(regex);
    const aboutValueinJSON = match[1];
    const foundItems = _.filter(
      [...range.getItems({ ignoreElementEnd: true })],
      (item) =>
        item.name === NUMBERED_CITATION &&
        item.getAttribute('resource') === aboutValueinJSON
    );

    this.updateToANewFirstCitedCitation(foundItems, getCitationDataForReOrder);
  }

  /**
   * Accept Split:span and Split:list suggestions when right hand panel is opened/closed
   * @public
   * @returns { void }
   */
  public acceptSplitSuggestion(): void {
    const { plugins } = this.citationService.ckeditorInstanceFromEditorComp;
    const trackChangesPlugin = plugins.get(TRACK_CHANGES);
    const trackChangesEditingPlugin = plugins.get(TRACK_CHANGES_EDITING);
    // Zero attached items alone doesn't prove a suggestion is a Split marker - pasted
    // content (e.g. bullet lists) can transiently produce real insertion suggestions
    // with zero items too, so the description must also match Split:* before accepting.
    const splitDescriptions = new Set([
      CommonConstants.splitSpan,
      CommonConstants.splitPara,
    ]);
    const suggestionsToAccept = trackChangesPlugin
      .getSuggestions({ skipNotAttached: true, toJSON: true })
      .filter((suggestion) => {
        if (this.suggestionsToDiscard.includes(suggestion.id)) {
          return true;
        }
        const trackSuggestion = trackChangesPlugin.getSuggestion(suggestion.id);
        return (
          !trackSuggestion.getItems().length &&
          trackChangesEditingPlugin.descriptionFactory
            .getDescriptions([trackSuggestion])
            .some(({ content }) => splitDescriptions.has(content))
        );
      });
    _.forEach(suggestionsToAccept, (suggestion: ISuggestion) => {
      trackChangesEditingPlugin.acceptSuggestion(
        trackChangesPlugin.getSuggestion(suggestion.id)
      );
    });
  }

  /**
   * Cleans up all suggestion marker attributes after accept/discard operations.
   * Removes data-suggestion, data-suggestion-start-before, data-suggestion-end-before,
   * data-suggestion-start-after, and data-suggestion-end-after attributes from the DOM.
   *
   * @param ckeditor - The CKEditor component instance
   * @param suggestionId - The ID of the suggestion to clean up
   * @public
   * @returns { void }
   */
  public cleanupSuggestionMarkers(
    ckeditor: CKEditorComponent,
    suggestionId: string
  ): void {
    if (!ckeditor?.editorInstance) {
      return;
    }

    try {
      const range = ckeditor.editorInstance.model.createRangeIn(
        ckeditor.editorInstance.model.document.getRoot()
      );
      ckeditor.editorInstance.model.change((writer) => {
        const suggestionAttributes = [
          CommonConstants.trackchangesMarkup.SUGGESTION,
          CommonConstants.trackchangesMarkup.SUGGESTION_START_BEFORE,
          CommonConstants.trackchangesMarkup.SUGGESTION_END_BEFORE,
          CommonConstants.trackchangesMarkup.SUGGESTION_START_AFTER,
          CommonConstants.trackchangesMarkup.SUGGESTION_END_AFTER,
        ];

        for (const item of range.getItems()) {
          if (!item?.hasAttribute || !item?.getAttribute) {
            continue;
          }

          for (const attr of suggestionAttributes) {
            const attrValue = item.getAttribute(attr);
            if (attrValue?.toString().includes(suggestionId)) {
              writer.removeAttribute(attr, item);
            }
          }
        }
      });
    } catch (error) {
      throw new Error(
        `Error while cleaning up corrupted suggestion on accept and discard : ${error}`
      );
    }
  }

  public updateTitleInDocument(title = ''): void {
    try {
      this.store.dispatch(setCMTitleValue({ cmTitle: title }));
      const editor = this.citationService.ckeditorInstanceFromEditorComp;
      if (!editor || !title) {
        return;
      }
      const root = editor.model.document.getRoot();
      const header = [...editor.model.createRangeIn(root).getItems()].find(
        (item) => item.name === 'header'
      );
      const heading =
        header &&
        [...header.getChildren()].find((child) => child.name === 'heading1');

      if (heading) {
        editor.model.change((writer) => {
          writer.remove(writer.createRangeIn(heading));
          writer.insertText(title, heading, 0);
        });
      }
    } catch (error) {
      throw new Error(`Error while updating title in document : ${error}`);
    }
  }

  /**
   * Normalizes nested ref spans by moving inner span.ref nodes out as siblings
   * of the outer ref span, preserving both outer and inner citation elements.
   *
   * @param {Document} documentFragment - Parsed HTML document
   * @private
   * @returns {void}
   */
  private removeNestedRefSpans(documentFragment: Document): void {
    const refSpans = Array.from(
      documentFragment.querySelectorAll(NUMBERED_CITATION_QUERYSELECTOR)
    );

    for (const refSpan of refSpans) {
      const parentNode = refSpan.parentNode;
      if (!parentNode) {
        continue;
      }

      // Hoist any nested ref span to be a sibling of the outer ref span.
      let insertAfterNode: ChildNode = refSpan;
      let nestedRefSpan = refSpan.querySelector(
        NUMBERED_CITATION_QUERYSELECTOR
      );

      while (nestedRefSpan) {
        parentNode.insertBefore(nestedRefSpan, insertAfterNode.nextSibling);
        insertAfterNode = nestedRefSpan;
        nestedRefSpan = refSpan.querySelector(NUMBERED_CITATION_QUERYSELECTOR);
      }
    }
  }

  /**
   * Ensures all citation-related "about" attributes reference the correct baseHref.
   */
  public normalizeCitationAboutAttributes(editorHtml: string): string {
    // Return as-is if no content or baseHref is unavailable
    if (!editorHtml || !this.docBaseHref) {
      return editorHtml;
    }

    const documentFragment = this.parser.parseFromString(
      editorHtml,
      'text/html'
    );

    // Remove nested ref spans first to clean up any malformed citation markup
    this.removeNestedRefSpans(documentFragment);

    // Locate citation container elements
    const citationElements = documentFragment.querySelectorAll(
      NUMBERED_CITATION_QUERYSELECTOR
    );

    if (!citationElements.length) {
      return editorHtml;
    }

    // Iterate through each citation container
    _.forEach(citationElements, (citationElement) => {
      const citationId = citationElement.getAttribute('id');
      if (!citationId) {
        return; // lodash uses return instead of continue
      }

      const citationIdSuffix = '#' + citationId;
      const correctAboutValue = this.docBaseHref + citationIdSuffix;

      const outerAbout = citationElement.getAttribute('about');

      // Ensure outer citation span has correct about reference
      if (
        outerAbout &&
        (!outerAbout.startsWith(this.docBaseHref) ||
          !outerAbout.endsWith(citationIdSuffix))
      ) {
        citationElement.setAttribute('about', correctAboutValue);
      }

      // Process inner spans that contain RDFa attributes
      const innerCitationSpans =
        citationElement.querySelectorAll('span[about]');

      _.forEach(innerCitationSpans, (innerSpan) => {
        const currentAbout = innerSpan.getAttribute('about');
        if (!currentAbout) {
          return;
        }

        const hashIndex = currentAbout.indexOf('#');
        // Fix malformed about values that do not contain an id suffix
        if (hashIndex === -1) {
          innerSpan.setAttribute('about', correctAboutValue);
          return;
        }
        // Skip if already correct
        if (
          currentAbout.startsWith(this.docBaseHref) &&
          currentAbout.endsWith(citationIdSuffix)
        ) {
          return;
        }

        const aboutAttributeSuffix = currentAbout.substring(hashIndex);
        // Correct incorrect baseHref while preserving suffix
        if (aboutAttributeSuffix !== citationIdSuffix) {
          innerSpan.setAttribute('about', this.docBaseHref + citationIdSuffix);
        }

        if (innerSpan.getAttribute('property') === 'frbr:partOf') {
          const currentResource = innerSpan.getAttribute('resource');

          if (currentResource !== this.docBaseHref) {
            innerSpan.setAttribute('resource', this.docBaseHref);
          }
        }
      });
    });
    // returning  updated HTML content as string (body only)
    return documentFragment.body.innerHTML;
  }

  private isMediaElement(selectedElement): boolean {
    return (
      selectedElement?.name === INLINE_IMAGE ||
      selectedElement?.name === INLINE_AUDIO ||
      (selectedElement?.name === FIGURE_TAG &&
        selectedElement?.getAttribute?.('class')?.includes?.(MEDIA_CLASS))
    );
  }

  // Disables text formatting plugins when a media element is selected or clicked inside.
  public disableFormattingPlugins(ckeditorInstance, srcElement = null): void {
    const selection = ckeditorInstance?.model?.document?.selection;
    if (!selection || !ckeditorInstance?.commands) {
      return;
    }

    const range = selection.getFirstRange?.();
    const rangeItems = range ? [...(range.getItems?.() ?? [])] : [];
    const selectedElement = selection.getSelectedElement?.();
    // includeSelf is required because in inline track-change mode the anchor's parent is often the media element itself, not just an ancestor of it.
    const isClickInsideMediaElement = selection.anchor?.parent
      ?.getAncestors({ includeSelf: true })
      .some((ele) => this.isMediaElement(ele));
    // Selection can collapse anywhere inside a media element (or its inline track-change markup), so an ancestor check is needed in addition to range/selected-element checks.
    const isMediaElementSelected =
      (srcElement &&
        [
          CK_MEDIA_IMAGE,
          inlineMediaClass,
          MEDIA_CLASS,
          CK_MEDIA_VIDEO,
        ].includes(srcElement?.getAttribute?.('class'))) ||
      Boolean(isClickInsideMediaElement) ||
      (rangeItems.length > 1
        ? rangeItems.some((item) => this.isMediaElement(item))
        : this.isMediaElement(selectedElement));

    _.forEach(TEXT_FORMATTING_PLUGINS, (commandName) => {
      const command = ckeditorInstance.commands.get(commandName);
      if (!command) {
        return;
      }

      // forceDisabled/clearForceDisabled survive CKEditor's own refresh() calls; isEnabled is set too as a guaranteed fallback for commands without that API.
      if (isMediaElementSelected) {
        command.forceDisabled?.(MEDIA_SELECTION_DISABLED);
        command.isEnabled = false;
      } else {
        command.clearForceDisabled?.(MEDIA_SELECTION_DISABLED);
        command.isEnabled = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
